import React, { useState, useEffect, useMemo, useCallback } from "react";
import Card from "../components/Card";
import Sidebar from "../components/Sidebar";
import Button from "../components/Button";
import { mlmAPI } from "../services/api";

const SearchIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const ChevronDownIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const StatusBadge = ({ status }) => {
  const getStatusClass = (s) => {
    switch (String(s || "").toLowerCase()) {
      case "active":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "pending":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "inactive":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-gray-500/20 text-[var(--text-secondary)] border-gray-400/30";
    }
  };

  return (
    <span
      className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${getStatusClass(
        status,
      )}`}
    >
      {status}
    </span>
  );
};

const SORT_OPTIONS = [
  "Volume (high → low)",
  "Leg earnings (high → low)",
  "Personal earnings (high → low)",
  "Name (A–Z)",
  "Join date (newest)",
];

const DirectLegBusiness = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [sortBy, setSortBy] = useState("Volume (high → low)");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchLegs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await mlmAPI.getDirectLegBusiness();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.error || e?.message || "Failed to load direct legs.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLegs();
  }, [fetchLegs]);

  const filteredSorted = useMemo(() => {
    let list = rows.filter((leg) => {
      const name = String(leg.name || "").toLowerCase();
      const matchesSearch = name.includes(searchTerm.trim().toLowerCase());
      const matchesStatus =
        statusFilter === "All Statuses" || leg.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    const cmpNum = (a, b, key) => Number(b[key] || 0) - Number(a[key] || 0);
    const cmpStr = (a, b, key) =>
      String(a[key] || "").localeCompare(String(b[key] || ""), undefined, {
        sensitivity: "base",
      });

    list = [...list];
    switch (sortBy) {
      case "Volume (high → low)":
        list.sort((a, b) => cmpNum(a, b, "legVolumeUsd"));
        break;
      case "Leg earnings (high → low)":
        list.sort((a, b) => cmpNum(a, b, "legTotalEarnings"));
        break;
      case "Personal earnings (high → low)":
        list.sort((a, b) => cmpNum(a, b, "personalEarnings"));
        break;
      case "Name (A–Z)":
        list.sort((a, b) => cmpStr(a, b, "name"));
        break;
      case "Join date (newest)":
        list.sort(
          (a, b) =>
            new Date(b.joinDate || 0).getTime() - new Date(a.joinDate || 0).getTime(),
        );
        break;
      default:
        break;
    }
    return list;
  }, [rows, searchTerm, statusFilter, sortBy]);

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-1">
              Direct leg business
            </h1>
            <p className="text-sm text-[var(--text-primary)] opacity-60">
              Each row is a direct referral and their referral subtree: confirmed deposit volume,
              member counts, and summed profile earnings under that leg.
            </p>
          </div>
          <Button variant="secondary" onClick={fetchLegs} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>

        {error ? (
          <Card className="p-4 mb-6 border border-red-500/30">
            <p className="text-red-400 font-medium">{error}</p>
            <Button className="mt-3" onClick={fetchLegs}>
              Retry
            </Button>
          </Card>
        ) : null}

        {/* Filter Bar */}
        <div className="mb-6">
          <Card className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative lg:col-span-2">
                <SearchIcon className="w-5 h-5 text-[var(--text-primary)] opacity-40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by leg name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-[var(--bg-card)] border border-primary/30 rounded-xl pl-10 pr-4 py-2.5 text-[var(--text-primary)] text-sm placeholder-[#22c55e]/40 focus:outline-none focus:border-primary transition-all"
                />
              </div>

              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none w-full bg-[var(--bg-card)] border border-primary/30 rounded-xl px-4 py-2.5 pr-10 text-[var(--text-primary)] text-sm font-medium focus:outline-none focus:border-primary transition-all cursor-pointer"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="w-4 h-4 text-[var(--text-primary)] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>

              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none w-full bg-[var(--bg-card)] border border-primary/30 rounded-xl px-4 py-2.5 pr-10 text-[var(--text-primary)] text-sm font-medium focus:outline-none focus:border-primary transition-all cursor-pointer"
                >
                  <option value="All Statuses">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <ChevronDownIcon className="w-4 h-4 text-[var(--text-primary)] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-6">
          <h2 className="text-lg md:text-xl font-bold text-[var(--text-primary)] mb-2">
            Direct leg performance
          </h2>
          <p className="text-xs text-[var(--text-primary)] opacity-50 mb-6">
            Volume = sum of confirmed crypto deposits (USD) for everyone in that leg&apos;s subtree.
          </p>

          {loading ? (
            <div className="py-16 text-center text-[var(--text-primary)] opacity-60">
              Loading direct legs…
            </div>
          ) : null}

          {/* Desktop Table View */}
          {!loading && (
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-primary/20">
                    <th className="text-left py-4 px-4 text-xs font-semibold text-[var(--text-primary)] opacity-60 uppercase tracking-wider">
                      Leg name
                    </th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-[var(--text-primary)] opacity-60 uppercase tracking-wider">
                      Leg volume (USD)
                    </th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-[var(--text-primary)] opacity-60 uppercase tracking-wider">
                      Members (active / total)
                    </th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-[var(--text-primary)] opacity-60 uppercase tracking-wider">
                      Join date
                    </th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-[var(--text-primary)] opacity-60 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-[var(--text-primary)] opacity-60 uppercase tracking-wider">
                      Leg earnings (sum)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSorted.map((leg) => (
                    <tr
                      key={leg.id}
                      className="border-b border-primary/10 hover:bg-gradient-to-r hover:from-[#22c55e]/5 hover:to-transparent transition-all duration-200"
                    >
                      <td className="py-4 px-4 text-[var(--text-primary)] font-medium">
                        {leg.name}
                      </td>
                      <td className="py-4 px-4 text-[var(--text-primary)] font-bold">
                        $
                        {Number(leg.legVolumeUsd || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="py-4 px-4 text-[var(--text-primary)]">
                        {Number(leg.legActiveMembers || 0)} /{" "}
                        {Number(leg.legMemberCount || 0)}
                      </td>
                      <td className="py-4 px-4 text-[var(--text-primary)] opacity-60 text-sm">
                        {leg.joinDate
                          ? new Date(leg.joinDate).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="py-4 px-4">
                        <StatusBadge status={leg.status} />
                      </td>
                      <td className="py-4 px-4 text-[var(--text-primary)] font-bold">
                        $
                        {Number(leg.legTotalEarnings || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile Card View */}
          {!loading && (
            <div className="md:hidden space-y-4">
              {filteredSorted.map((leg) => (
                <div
                  key={leg.id}
                  className="bg-[var(--bg-card)] rounded-xl p-4 border border-primary/20 hover:border-primary/40 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-base font-bold text-[var(--text-primary)]">{leg.name}</h3>
                    <StatusBadge status={leg.status} />
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">
                        Leg volume
                      </p>
                      <p className="text-lg font-bold text-[var(--text-primary)]">
                        $
                        {Number(leg.legVolumeUsd || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">
                        Leg earnings
                      </p>
                      <p className="text-lg font-bold text-[var(--text-primary)]">
                        $
                        {Number(leg.legTotalEarnings || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm border-t border-primary/10 pt-3">
                    <div>
                      <p className="text-xs text-[var(--text-primary)] opacity-60">Members</p>
                      <p className="text-[var(--text-primary)] font-medium mt-0.5">
                        {Number(leg.legActiveMembers || 0)} active /{" "}
                        {Number(leg.legMemberCount || 0)} total
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[var(--text-primary)] opacity-60">Join date</p>
                      <p className="text-[var(--text-primary)] font-medium mt-0.5">
                        {leg.joinDate
                          ? new Date(leg.joinDate).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredSorted.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[var(--text-primary)] opacity-60">
                {rows.length === 0
                  ? "No direct referrals yet. Share your link to build legs."
                  : "No results match your filters."}
              </p>
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
};

export default DirectLegBusiness;
